'use client'

import { useEffect, useState, useCallback } from 'react'
import { CardContent, CardHeader, CardTitle, CardV2 } from '../../../components/CardV2/CardV2'
import { useTranslation } from 'react-i18next'
import { toastService } from "../../../services/ToastService";
import { Loader2, CreditCard, Smartphone, Wallet, Banknote } from 'lucide-react'
import PaymentsAPI, { PaymentMethodPreferenceRequest, PaymentMethodInfo as APIPaymentMethodInfo } from '../../../api/PaymentsAPI'
import Switch from '../../../components/Switch/Switch'
import { LabelV2 } from '../../../components/LabelV2/LabelV2'

interface PaymentMethodInfo {
  key: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  enabled: boolean;
  configId?: string;
}

export default function PaymentMethodsTab() {
  const { t } = useTranslation();
  const [paymentMethods, setPaymentMethods] = useState<Record<string, APIPaymentMethodInfo>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [updatingMethods, setUpdatingMethods] = useState<Set<string>>(new Set());

  const loadPaymentMethodConfigurations = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await PaymentsAPI.getPaymentMethodConfigurations();
      setPaymentMethods(response.data.data);
    } catch (error) {
      console.error('Error loading payment method configurations:', error);
      toastService.showToast(t("settings.payments.methods.errors.load_failed"), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadPaymentMethodConfigurations();
  }, [loadPaymentMethodConfigurations]);

  const updatePaymentMethodPreference = async (pmcId: string, methodKey: string, enabled: boolean) => {
    const preference: PaymentMethodPreferenceRequest = {
      preference: enabled ? 'on' : 'off'
    };

    setUpdatingMethods(prev => new Set(prev).add(methodKey));
    
    try {
      const response = await PaymentsAPI.updatePaymentMethodPreference(pmcId, methodKey, preference);
      
      if (response.data.success) {
        const methodKeyMapping: Record<string, string> = {
          'card': 'card',
          'blik': 'blik',
          'p24': 'p24',
          'bancontact': 'bancontact',
          'sofort': 'sofort',
          'ideal': 'ideal',
          'sepa_debit': 'sepaDebit',
          'apple_pay': 'applePay',
          'google_pay': 'googlePay',
          'link': 'link'
        };
        
        const apiFieldName = methodKeyMapping[methodKey];
        if (apiFieldName) {
          setPaymentMethods(prev => ({
            ...prev,
            [apiFieldName]: {
              ...prev[apiFieldName],
              enabled: enabled
            }
          }));
        }
        
        toastService.showToast(
          t(enabled ? "settings.payments.methods.success.enabled" : "settings.payments.methods.success.disabled", { method: getMethodDisplayName(methodKey) }), 
          'success'
        );
      } else {
        throw new Error(response.data.error || 'Update failed');
      }
    } catch (error) {
      console.error('Error updating payment method preference:', error);
      toastService.showToast(t("settings.payments.methods.errors.update_failed"), 'error');
    } finally {
      setUpdatingMethods(prev => {
        const newSet = new Set(prev);
        newSet.delete(methodKey);
        return newSet;
      });
    }
  };

  const getMethodDisplayName = (methodKey: string): string => {
    const methodNames: Record<string, string> = {
      'card': t("settings.payments.methods.names.card"),
      'blik': t("settings.payments.methods.names.blik"),
      'p24': t("settings.payments.methods.names.p24"),
      'bancontact': t("settings.payments.methods.names.bancontact"),
      'sofort': t("settings.payments.methods.names.sofort"),
      'ideal': t("settings.payments.methods.names.ideal"),
      'sepa_debit': t("settings.payments.methods.names.sepa_debit"),
      'apple_pay': t("settings.payments.methods.names.apple_pay"),
      'google_pay': t("settings.payments.methods.names.google_pay"),
      'link': t("settings.payments.methods.names.link"),
    };
    return methodNames[methodKey] || methodKey.toUpperCase();
  };

  const getMethodIcon = (methodKey: string): React.ReactNode => {
    const iconProps = { className: "h-5 w-5" };
    
    switch (methodKey) {
      case 'card':
        return <CreditCard {...iconProps} />;
      case 'apple_pay':
      case 'google_pay':
      case 'blik':
        return <Smartphone {...iconProps} />;
      case 'p24':
      case 'bancontact':
      case 'sofort':
      case 'ideal':
      case 'sepa_debit':
      case 'link':
        return <Banknote {...iconProps} />;
      default:
        return <Wallet {...iconProps} />;
    }
  };

  const getMethodDescription = (methodKey: string): string => {
    const descriptions: Record<string, string> = {
      'card': t("settings.payments.methods.descriptions.card"),
      'blik': t("settings.payments.methods.descriptions.blik"),
      'p24': t("settings.payments.methods.descriptions.p24"),
      'bancontact': t("settings.payments.methods.descriptions.bancontact"),
      'sofort': t("settings.payments.methods.descriptions.sofort"),
      'ideal': t("settings.payments.methods.descriptions.ideal"),
      'sepa_debit': t("settings.payments.methods.descriptions.sepa_debit"),
      'apple_pay': t("settings.payments.methods.descriptions.apple_pay"),
      'google_pay': t("settings.payments.methods.descriptions.google_pay"),
      'link': t("settings.payments.methods.descriptions.link"),
    };
    return descriptions[methodKey] || t("settings.payments.methods.descriptions.default");
  };

  const getAvailablePaymentMethods = (): PaymentMethodInfo[] => {
    const methodKeyMapping: Record<string, string> = {
      'card': 'card',
      'blik': 'blik',
      'p24': 'p24',
      'bancontact': 'bancontact',
      'sofort': 'sofort',
      'ideal': 'ideal',
      'sepa_debit': 'sepaDebit',
      'apple_pay': 'applePay',
      'google_pay': 'googlePay',
      'link': 'link'
    };
    
    return Object.entries(methodKeyMapping).map(([methodKey, apiFieldName]) => {
      const methodData = paymentMethods[apiFieldName];
      
      return {
        key: methodKey,
        name: getMethodDisplayName(methodKey),
        description: getMethodDescription(methodKey),
        icon: getMethodIcon(methodKey),
        enabled: methodData?.enabled || false,
        configId: methodData?.pmcId
      };
    });
  };

  const handleMethodToggle = (methodKey: string, enabled: boolean) => {
    // Find the method info to get the correct config ID
    const availableMethods = getAvailablePaymentMethods();
    const methodInfo = availableMethods.find(method => method.key === methodKey);
    
    if (!methodInfo || !methodInfo.configId) {
      toastService.showToast(t("settings.payments.methods.errors.no_active_config"), 'error');
      return;
    }

    updatePaymentMethodPreference(methodInfo.configId, methodKey, enabled);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-10 space-y-4">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <div className="text-center">
          <p className="font-medium text-muted-foreground">{t("settings.payments.methods.loading")}</p>
        </div>
      </div>
    );
  }

  const availableMethods = getAvailablePaymentMethods();

  return (
    <div className="space-y-6">
      <CardV2>
        <CardHeader>
          <CardTitle>{t("settings.payments.methods.title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-muted-foreground">
            {t("settings.payments.methods.description")}
          </p>
          
          {availableMethods.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                {t("settings.payments.methods.no_methods_available")}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {availableMethods.map((method) => (
              <div key={method.key} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="text-muted-foreground">
                    {method.icon}
                  </div>
                  <div>
                    <LabelV2 htmlFor={`method-${method.key}`} className="text-sm font-medium">
                      {method.name}
                    </LabelV2>
                    <p className="text-xs text-muted-foreground">
                      {method.description}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {updatingMethods.has(method.key) && (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                  <Switch
                    checked={method.enabled}
                    onChange={() => handleMethodToggle(method.key, !method.enabled)}
                  />
                </div>
              </div>
            ))}
            </div>
          )}
        </CardContent>
      </CardV2>

      <CardV2>
        <CardHeader>
          <CardTitle>{t("settings.payments.methods.info.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>{t("settings.payments.methods.info.note1")}</p>
            <p>{t("settings.payments.methods.info.note2")}</p>
            <p>{t("settings.payments.methods.info.note3")}</p>
          </div>
        </CardContent>
      </CardV2>
    </div>
  );
}
